// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * Keyset pagination as a "load more" list (design doc §9.1).
 *
 * The API pages by opaque cursor, not by offset, so there are no page numbers to jump to —
 * a deliberate choice on the server side, since offsets skip or repeat rows when the list
 * changes under the reader, and these lists change all day. A forward-only list is the
 * honest shape for that contract.
 */

import { useInfiniteQuery } from "@tanstack/react-query";
import type { Page } from "@/api/types";

export function useCursorList<T>(
  queryKey: unknown[],
  fetchPage: (cursor?: string) => Promise<Page<T>>,
  options: { enabled?: boolean } = {},
) {
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: options.enabled ?? true,
  });

  return {
    ...query,
    items: (query.data?.pages ?? []).flatMap((page) => page.items),
  };
}
